import { organizationVendorTypes, vendorSortBy } from 'utils/proptypes';
import PaginateTable from 'components/table/paginate';
import { HiOutlineDuplicate, HiOutlinePencil } from 'react-icons/hi';
import {useEffect, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import { initOrganizationVendor } from 'utils/initData';
import SpinnerComponent from 'components/spinner';
import {defaultpageCount, permissionKeys, sortByTypeType} from 'utils/constants';
import {isEqual} from 'lodash';
import NoResult from 'components/commonComponent/noResult';
import {checkPermission, copyToClipboard} from '../../utils/utils';
import {useSelector} from 'react-redux';
import ActionTable from 'components/table/actionTable';
import {orgSelector, userSelector} from 'redux/selectors';
import '../../styles/styles.scss';
import {Table} from 'flowbite-react';
import Sort from 'components/table/sort';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import EmptyState from 'components/commonComponent/emptyState';
import { getOrgVendorsByFilter } from 'api/vendorApi';
import VendorModal from 'components/modal/vendorModal/vendorModal';

const categoryEmpty = require('../../assets/image/svg/document-text.svg').default || '';

const VendorList = (props: any) => {
    const queryClient = useQueryClient();
    const {WRITE_VENDOR, WRITE_ORG} = permissionKeys;
    const [t] = useTranslation();
    const {userInfo} = useSelector(userSelector);
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalEntities, setTotalEntities] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [vendors, setVendors] = useState([initOrganizationVendor]);
    const [targetVendor, setTargetVendor] = useState(initOrganizationVendor);
    const [sortBy, setSortBy] = useState<vendorSortBy | ''>('');
    const [sortByType, setSortByType] = useState('');
    const [pageCount, setPageCount] = useState(defaultpageCount);
    const {organizationId} = useSelector(orgSelector);
    const isWriteVendor = checkPermission(userInfo, props.type, [WRITE_VENDOR, WRITE_ORG], organizationId);

    const fetchVendors = () => {
        return getOrgVendorsByFilter({
            page: currentPage,
            limit: pageCount,
            searchQuery: searchValue,
            sortByType,
            organizationId,
            ...(sortBy ? { sortBy } : {}),
        });
    };

    const {data, isLoading, error, refetch} = useQuery(
        ['getOrgVendors', currentPage, searchValue, sortBy, sortByType, pageCount, organizationId],
        fetchVendors,
        {
            staleTime: Infinity,
            retry: false,
            refetchOnWindowFocus: false,
        },
    );

    useEffect(() => {
        if (error) {
            setVendors([initOrganizationVendor]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const handleSearch = (value: any) => {
        setSearchValue(value);
        setCurrentPage(0);
    };

    const handleCopyToClipboard = (e: any, item: any, type: string) => {
        switch (type) {
            case 'name':
                copyToClipboard(item.name, t('vendorPage.copiedName'));
                break;
            case 'description':
                copyToClipboard(item.description, t('vendorPage.copiedDescription'));
                break;
            case 'remitEmail':
                copyToClipboard(item.remitEmail, t('vendorPage.copiedEmail'));
                break;
            case 'format':
                copyToClipboard(item.format, t('vendorPage.copiedFormat'));
                break;
            case 'integration':
                copyToClipboard(item.integration, t('vendorPage.copiedIntegration'));
                break;
            case 'url':
                copyToClipboard(item.url, t('vendorPage.copiedUrl'));
                break;
        }
        e.stopPropagation();
    };

    const handleHeaderSort = (field: vendorSortBy) => {
        setSortBy(field);
        let type = '';
        if (field !== sortBy) {
            type = sortByTypeType.ASC;
        } else if (sortByType === sortByTypeType.ASC) {
            type = sortByTypeType.DESC;
        } else {
            setSortBy('');
        }
        setSortByType(type);
    };

    const handleEdit = async (item: any) => {
        if (isWriteVendor) {
            setTargetVendor(item);
            setOpenModal(true);
            setIsEdit(true);
        }
    }

    useEffect(() => {
        setVendors([]);
        setTotalEntities(0);
        setCurrentPage(0);
        refetch();
    }, [organizationId, refetch]);

    useEffect(() => {
        if (data) {
            setVendors(data?.data?.entities || []);
            setTotalEntities(data.data?.totalEntities || 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, error, data, currentPage, searchValue, sortBy, sortByType, pageCount]);

    useEffect(() => {
        setTotalEntities(data?.data?.totalEntities || 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    if (isLoading) {
        return null;
    }

    return (
        <>
            {error ? <EmptyState
                                 title={t('vendorPage.titleNoResult')}
                                 subtitle={t('vendorPage.subTitleNoResult')}
                                 icon={categoryEmpty}
                /> :
                <>
                    {!isEqual(vendors[0], initOrganizationVendor) && totalEntities === 0 && searchValue === ''
                        ?
                        <EmptyState
                                    title={t('vendorPage.titleNoResult')}
                                    subtitle={t('vendorPage.subTitleNoResult')}
                                    icon={categoryEmpty}
                        />
                        :
                        !isEqual(vendors[0], initOrganizationVendor) && totalEntities === 0
                            ?
                            (
                                <div className="p-[30px]">
                                    <ActionTable
                                        placeholderSearch="Search"
                                        handleSearch={handleSearch}
                                        searchValue={searchValue}
                                    />
                                    <NoResult/>
                                </div>
                            )
                            :
                            (
                                <div className="main-sub">
                                    <ActionTable
                                        placeholderSearch="Search"
                                        handleSearch={handleSearch}
                                    />
                                    <Table className="h-full overflow-y-scroll">
                                        <Table.Head className="text-xs text-gray-900 border-b-2 border-gray-100">
                                            <Table.HeadCell className="cursor-pointer bg-white font-medium text-xs px-3 w-30"
                                                            onClick={() => handleHeaderSort('name')}>
                                                <div className="flex items-center ">
                                                    {t('vendorPage.name').toUpperCase()}
                                                    <Sort check={sortBy === 'name'} sortByType={sortByType}/>
                                                </div>
                                            </Table.HeadCell>
                                            <Table.HeadCell
                                                className="cursor-pointer hidden-mobile w-30 bg-white font-medium text-xs px-3"
                                                onClick={() => {
                                                    handleHeaderSort('description');
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    {t('vendorPage.description').toUpperCase()}
                                                    <Sort check={sortBy === 'description'} sortByType={sortByType}/>
                                                </div>
                                            </Table.HeadCell>
                                            <Table.HeadCell
                                                className="hidden-mobile w-30 bg-white font-medium text-xs px-3"
                                            >
                                                <div className="flex items-center">
                                                    {t('vendorPage.email').toUpperCase()}
                                                </div>
                                            </Table.HeadCell>
                                            <Table.HeadCell
                                                className="hidden-mobile w-30 bg-white font-medium text-xs px-3"
                                            >
                                                <div className="flex items-center">
                                                    {t('vendorPage.format').toUpperCase()}
                                                </div>
                                            </Table.HeadCell>
                                            <Table.HeadCell
                                                className="hidden-mobile w-30 bg-white font-medium text-xs px-3"
                                            >
                                                <div className="flex items-center">
                                                    {t('vendorPage.integration').toUpperCase()}
                                                </div>
                                            </Table.HeadCell>
                                            <Table.HeadCell
                                                className="hidden-mobile w-30 bg-white font-medium text-xs px-3"
                                            >
                                                <div className="flex items-center">
                                                    {t('vendorPage.url').toUpperCase()}
                                                </div>
                                            </Table.HeadCell>
                                            <Table.HeadCell className="w-6 bg-white">{''}</Table.HeadCell>
                                        </Table.Head>
                                        <Table.Body>
                                            {vendors.map((item: organizationVendorTypes, index) => (
                                                <Table.Row key={`row-${index + 1}`}
                                                           className="bg-white text-sm hover:bg-gray-50 border-b border-gray-100 cursor-pointer">
                                                    <Table.Cell className="font-semibold py-2.5 px-3">
                                                        <div className="flex flex-row items-center"
                                                             onClick={() => handleEdit(item)}>
                                                            <div className="text-secondary">{item?.name}</div>
                                                            <div
                                                                className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                                <HiOutlineDuplicate size={25}
                                                                                    onClick={(e: any) => handleCopyToClipboard(e, item, 'name')}/>
                                                            </div>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="py-2.5 px-3">
                                                        <div className="flex flex-row items-center"
                                                             onClick={() => handleEdit(item)}>
                                                            <div className="text-black">{item?.description}</div>
                                                            <div
                                                                className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                                <HiOutlineDuplicate size={25}
                                                                                    onClick={(e: any) => handleCopyToClipboard(e, item, 'description')}/>
                                                            </div>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="py-2.5 px-3">
                                                        <div className="flex flex-row items-center"
                                                             onClick={() => handleEdit(item)}>
                                                            <div className="text-black">{item?.remitEmail}</div>
                                                            <div
                                                                className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                                <HiOutlineDuplicate size={25}
                                                                                    onClick={(e: any) => handleCopyToClipboard(e, item, 'remitEmail')}/>
                                                            </div>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="py-2.5 px-3">
                                                        <div className="flex flex-row items-center"
                                                             onClick={() => handleEdit(item)}>
                                                            <div className="text-black">{item?.format}</div>
                                                            <div
                                                                className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                                <HiOutlineDuplicate size={25}
                                                                                    onClick={(e: any) => handleCopyToClipboard(e, item, 'format')}/>
                                                            </div>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="py-2.5 px-3">
                                                        <div className="flex flex-row items-center"
                                                             onClick={() => handleEdit(item)}>
                                                            <div className="text-black">{item?.integration}</div>
                                                            <div
                                                                className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                                <HiOutlineDuplicate size={25}
                                                                                    onClick={(e: any) => handleCopyToClipboard(e, item, 'integration')}/>
                                                            </div>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="py-2.5 px-3">
                                                        <div className="flex flex-row items-center"
                                                             onClick={() => handleEdit(item)}>
                                                            <div className="text-black">{item?.url}</div>
                                                            <div
                                                                className="copy-icon pl-3 cursor-pointer text-gray-800">
                                                                <HiOutlineDuplicate size={25}
                                                                                    onClick={(e: any) => handleCopyToClipboard(e, item, 'url')}/>
                                                            </div>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="py-2.5">
                                                        {isWriteVendor && (
                                                            <GroupButtonWithIcons
                                                                className="items-center justify-center"
                                                                buttons={[
                                                                    {
                                                                        type: 'button',
                                                                        text: '',
                                                                        classType: 'white',
                                                                        icon: <HiOutlinePencil size={'20px'}/>,
                                                                        action: () => handleEdit(item),
                                                                    },
                                                                ]}
                                                            />
                                                        )}
                                                    </Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                    {isLoading && <SpinnerComponent/>}
                                    <PaginateTable
                                        setCurrentPage={setCurrentPage}
                                        currentPage={currentPage}
                                        totalEntities={totalEntities}
                                        isLoadingTable={isLoading}
                                    />
                                </div>
                            )
                    }
                </>}
            {openModal && (
                <VendorModal
                    headerTitle={isEdit ? t('vendorPage.editVendor') : t('vendorPage.addVendor')}
                    openModal={openModal}
                    isEdit={isEdit}
                    targetData={targetVendor}
                    setOpenModal={setOpenModal}
                    queryClient={queryClient}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                />
            )}
        </>
    );
};

export default VendorList;
